import React, { useContext } from 'react'
import PropTypes from 'prop-types'

import { Typography, Spacer, Divider, Box, FlexGrid } from '@telus-uds/components-web'

import { components, utils } from '@digital-commerce/shared-utils'
import useTranslate from '../../../hooks/useTranslate'

import { HeadingContainer, LineItemWrapper, MultipleLineItem, WrapInlineBox } from './styles'
import {
  getFlowType,
  getPriceOneTimeCredit,
  getPriceOneTimeTotal,
  checkIfCartHasHSItemsOnly,
  getPriceOneTimeSubtotal,
  getPriceOneTimeTaxes,
  getShippingValue
} from './utils'
import LineItem from './LineItem'
import localeContext from '../../../contexts/Locale'

const PST_TAX = 'PST'
const RESERVATION_FLOW = 'RESERVATION'

const DueTodaySection = ({ dataTestId, cart, Actions }) => {
  const { ShouldRender, WithLegal } = components
  const { lang, prov } = useContext(localeContext)
  const { requiresShipping } = utils.cart

  const translate = useTranslate()

  const taxes = getPriceOneTimeTaxes(cart)
  const hasHSItemsOnly = checkIfCartHasHSItemsOnly(cart)
  const withLegalFootnoteKey = 'priceSummary.lineItems.pricesMayChange'
  const termsAndConditions = 'priceSummary.lineItems.termsAndConditions'

  const isFirstTax = (taxIndex) => taxIndex === 0
  const itemsRequireShipping = requiresShipping(cart)
  const shouldShowShippingFeeHSOnly = hasHSItemsOnly && itemsRequireShipping

  const getTermsAndConditionsForTaxes = (taxIndex) => {
    if (!isFirstTax(taxIndex)) {
      return null
    }
    return <WithLegal content={translate(termsAndConditions)} copy={lang} />
  }

  const getDueText = () => {
    if (getFlowType(cart) === RESERVATION_FLOW) {
      const termsAndCondition = <WithLegal content={translate(withLegalFootnoteKey)} copy={lang} />
      return {
        text: translate('priceSummary.dueOnPickUp'),
        termsAndCondition
      }
    }

    return {
      text: translate('priceSummary.dueToday'),
      termsAndCondition: null
    }
  }

  const getTaxTypeLabel = (taxType) => {
    const taxLabel = 'priceSummary.lineItems.taxType'
    const province = `${prov.toUpperCase()}`
    if (taxType === PST_TAX) {
      return (
        translate(`${taxLabel}.${taxType}.${province}`) ||
        translate(`${taxLabel}.${taxType}.default`)
      )
    }
    return translate(`${taxLabel}.${taxType}`)
  }

  const setTextTypeLine = (tax) =>
    `${translate('priceSummary.lineItems.tax')} (${getTaxTypeLabel(tax.type)} ${tax.rate}${
      lang === 'fr' ? ' %' : '%'
    })`

  return (
    <>
      <FlexGrid>
        <FlexGrid.Row distribute="between">
          <WrapInlineBox>
            <Box vertical={1}>
              <HeadingContainer>
                <Typography variant={{ size: 'h3', colour: 'dark' }}>
                  {translate('priceSummary.heading')}
                </Typography>
              </HeadingContainer>
              <Box />
            </Box>
          </WrapInlineBox>
        </FlexGrid.Row>
      </FlexGrid>
      <Divider variant={{ decorative: true }} />
      <Spacer space={2} />

      <ShouldRender when={shouldShowShippingFeeHSOnly}>
        <LineItem
          dataIdAttribute={`${dataTestId}-price-summary-shipping-self-install`}
          text={translate('priceSummary.lineItems.shipping')}
          cost={getShippingValue(cart)}
          lang={lang}
        />
        <Spacer space={2} />
      </ShouldRender>

      <ShouldRender when={!hasHSItemsOnly && itemsRequireShipping}>
        <MultipleLineItem>
          <LineItem
            dataIdAttribute={`${dataTestId}-price-summary-today-subtotal`}
            text={translate('priceSummary.lineItems.subtotal')}
            cost={getPriceOneTimeSubtotal(cart) - getPriceOneTimeCredit(cart)}
            lang={lang}
          />

          <LineItem
            dataIdAttribute={`${dataTestId}-price-summary-shipping`}
            text={translate('priceSummary.lineItems.shipping')}
            cost={getShippingValue(cart)}
            lang={lang}
          />
        </MultipleLineItem>
        <ShouldRender when={taxes.length > 0}>
          <Spacer space={2} />
          <MultipleLineItem>
            {taxes.map((tax, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <ShouldRender key={`${tax}-${index}`} when={Boolean(tax.rate)}>
                <LineItem
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${tax.type}-${tax.rate}-${index}`}
                  text={setTextTypeLine(tax)}
                  cost={tax.amount}
                  lang={lang}
                  termsAndCondition={getTermsAndConditionsForTaxes(index)}
                  dataIdAttribute={`${dataTestId}-price-summary-today-tax-${tax.type.toLowerCase()}`}
                />
              </ShouldRender>
            ))}
          </MultipleLineItem>
        </ShouldRender>
        <Spacer space={2} />
      </ShouldRender>
      <LineItemWrapper>
        <LineItem
          dataIdAttribute={`${dataTestId}-price-summary-today-total`}
          text={getDueText().text}
          termsAndCondition={getDueText().termsAndCondition}
          cost={getPriceOneTimeTotal(cart) - getPriceOneTimeCredit(cart)}
          lang={lang}
          bold
          amountBold
        />
      </LineItemWrapper>
      {Actions && <LineItemWrapper>{Actions}</LineItemWrapper>}
    </>
  )
}

DueTodaySection.propTypes = {
  dataTestId: PropTypes.string.isRequired,
  cart: PropTypes.object.isRequired,
  Actions: PropTypes.node
}

DueTodaySection.defaultProps = {
  Actions: undefined
}

export default DueTodaySection
